import { Component, HostBinding, HostListener, Inject, ViewChild } from '@angular/core';
import { UserResponse } from '../../services/auth/responses/user.response';
import { IConfigurationService } from '../../services/configuration/configuration-service.interface';
import { EventsServiceInterface } from '../../services/events/events.interface';
import { ISidebarService } from '../../services/sidebar/sidebar.interface';
import { IMenuItem } from '@platform500services/p500-ui-kit';
import { Router } from '@angular/router';
import { DestroySubscribers } from '../../decorators/destroy-subscribers.decorator';
import { MENU_CONFIG } from './sidebar.config-menu';
import { RbacServiceInterface } from '../../services/rbac/rbac-service.interface';
import { AuthServiceInterface } from '../../services/auth/auth-service.interface';
import { changelogData } from '../../../../changelog';
import { getSettingsContent } from '../../../modules/settings/settings/settings-page/settings-page.content';
import { IDataService } from '../../services/data/data-service.interface';
import { RbacPermissionsConstant } from '../../services/rbac/rbac-permissions.constant';
import { ITableView } from '../facade/service-facade/data.response';
import moment from 'moment-timezone';
import { BrowserWindowRef } from '../../services/window/window.service';

@Component({
  selector: 'gtd-sidebar',
  templateUrl: './sidebar.component.html',
})
@DestroySubscribers()
export class SidebarComponent {
  public sidebarOverlay = false;
  private subscribers: any = {};
  public user: UserResponse;
  @HostBinding('class') className = 'c-sidebar';
  @HostBinding('class.-open-selector') public isOpenSelector = false;
  @ViewChild('searchInner') insideElement;
  public menu: IMenuItem[];
  public isCollapsedNavigation = false;
  public currentSection?: IMenuItem;
  public sidebarMenu: IMenuItem[];
  public changelogVersion: string;
  public searchData: IMenuItem[];
  public year: string;

  constructor(
    private router: Router,
    @Inject('EventsServiceInterface') private eventsService: EventsServiceInterface,
    @Inject('IConfigurationService') public configurationService: IConfigurationService,
    @Inject('ISidebarService') public sidebarService: ISidebarService,
    @Inject('RbacServiceInterface') private rbacService: RbacServiceInterface,
    @Inject('AuthServiceInterface') public authService: AuthServiceInterface,
    @Inject('IDataService') private dataService: IDataService,
    private window: BrowserWindowRef
  ) {
    this.user = this.authService.userData();
    if (this.user) {
      const drillDownMenu = this.dataService.getData().drill_down_menu;
      if (drillDownMenu?.length) {
        MENU_CONFIG.find((item) => item.title === $localize`:@@common.reports:Reports`).children.push(
          drillDownMenu.map((item) => ({
            title: item.name,
            link: '/app/reporting/drilldown/' + item.hash,
            permission: RbacPermissionsConstant.PAGES_SPECIAL_DRILLDOWN,
          }))
        );
      }
      let menu = this.filterMenu(MENU_CONFIG);
      const tableViews = this.dataService.getData().table_views;
      if (tableViews?.length) {
        menu = this.filterMenu(addTableView(menu, tableViews));
      }
      this.sidebarMenu = this.menu = menu;
    }

    this.subscribers.isShownSidebar = this.eventsService.on('sidebarComponent:toggle').subscribe(() => {
      this.sidebarOverlay = !this.sidebarOverlay;
    });

    this.changelogVersion = changelogData[0]?.version;

    const items = Object.values(getSettingsContent(rbacService)).reduce((acc, values: any[]) => acc.concat(values));
    this.searchData = this.sidebarMenu.concat({ title: 'Settings', children: items });

    const timeZone = authService.userData().timezone || 'Etc/UTC';
    this.year = moment().tz(timeZone).format('Y');
  }

  @HostListener('document:click', ['$event'])
  @HostListener('document:touch', ['$event'])
  handleOutsideClick() {
    this.closeSection();
  }

  filterMenu(menuItems: IMenuItem[]): IMenuItem[] {
    const canMenuItem = (menuItem: IMenuItem) => {
      if (menuItem.showOnlyDemo && !this.configurationService.data().isDemo) {
        return false;
      }
      if (!menuItem.permission) {
        return true;
      }
      return this.rbacService.can(menuItem.permission);
    };
    return menuItems.reduce((list: IMenuItem[], item: IMenuItem) => {
      if (canMenuItem(item)) {
        const children = item.children?.map((values: IMenuItem[]) => values.filter(canMenuItem))?.filter((values) => values?.length);
        if (children?.length || item.link) {
          if (children) {
            return children?.length ? [...list, { ...item, children }] : list;
          } else {
            return [...list, item];
          }
        }
      }
      return list;
    }, []);
  }

  toggleSidebar(): void {
    if (this.sidebarOverlay) {
      this.eventsService.broadcast('sidebarComponent:toggle');
    }
  }

  toggleNavigation() {
    this.isCollapsedNavigation = !this.isCollapsedNavigation;
    this.eventsService.broadcast('sidebarComponent:collapsed-toggle', this.isCollapsedNavigation);
    this.closeSection();
  }

  openSection(section: IMenuItem, $event: MouseEvent) {
    $event.stopPropagation();
    this.currentSection = section;
    if (section.link) {
      this.router.navigateByUrl(section.link);
    }
  }

  closeSection() {
    this.currentSection = null;
  }

  isActiveSection(section: IMenuItem) {
    return (
      (section.link && this.router.isActive(section.link, false)) ||
      section.children?.some((item) => item.some((deepItem) => this.router.isActive(deepItem.link, false)))
    );
  }

  selectSearchItem(item) {
    this.router.navigate([item.link]);
  }

  public installPwa() {
    (this.window.nativeWindow.installPwaEvent as any).prompt();
  }
}

const addTableView = (menu: IMenuItem[], settings: ITableView[]): IMenuItem[] => {
  const sortedSettings = JSON.parse(JSON.stringify(settings)).sort((a, b) => b.sorting - a.sorting);
  const copyMenu = JSON.parse(JSON.stringify(menu));
  for (const item of copyMenu) {
    if (item.children) {
      const newItems = [];
      for (const child of item.children) {
        for (const value of sortedSettings) {
          if (child.length) {
            const isMatch = child.find((childItem) => childItem?.table === value.table);
            if (isMatch) {
              const menuItem: IMenuItem = {
                title: value.title,
                link: isMatch.link + '-views/' + value.alias,
              };
              newItems.push(menuItem);
            }
          }
        }
      }
      item.children = [newItems].concat(item.children);
    }
  }
  return copyMenu;
};
